import React, { Fragment, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { Redirect, Route, Router, Switch } from "wouter";

import { Meta } from "../blocks/Meta/Meta";
import { NoData } from "../blocks/NoData/NoData";
import { NewPackageDialog } from "../blocks/PackageEditor/NewPackageDialog/NewPackageDialog";
import { usePackageEditorSetting } from "../hooks/usePackageEditorSetting";
import { useLanguage } from "../i18n/useLanguage";
import { DealerCartPage } from "../pages/DealerCartPage";
import { DealerComparePrintPage } from "../pages/DealerComparePrintPage";
import { DealerContactUsPage } from "../pages/DealerContactUsPage";
import { DealerHomePage } from "../pages/DealerHomePage";
import { DealerOrderReviewPage } from "../pages/DealerOrderReviewPage";
import { DealerPackageEditorPage } from "../pages/DealerPackageEditorPage";
import { DealerProductDetailsPage } from "../pages/DealerProductDetailsPage";
import { DealerResultsPage } from "../pages/DealerResultsPage";
import { DealerWishlistPrintPage } from "../pages/DealerWishlistPrintPage";
import { useDealerAccountInfo } from "../stores/hooks/useDealerAccountInfo";
import { useDealerProducts } from "../stores/hooks/useDealerProducts";
import { useIsLoggedOn } from "../stores/hooks/useIsLoggedOn";

export const DealerHomePageRoute = "/:dealerUrlCode";
export const DealerLanguagePageRoute = "/:dealerUrlCode/:language";
export const DealerLandingPageRoute =
  "/:dealerUrlCode/:language/dealer/:dealerId";
export const DealerProductRoute =
  "/:dealerUrlCode/:language/dealer/:dealerId/product/:productType";
export const DealerVehicleResultsRoute =
  "/:dealerUrlCode/:language/dealer/:dealerId/product/:productType/type/:searchType/year/:vehicleYear/make/:vehicleManufacturerName/category/:vehicleModelClassId/model/:vehicleModelName/trim/:vehicleSubmodel";
export const DealerVehicleResultsWithoutTrimRoute =
  "/:dealerUrlCode/:language/dealer/:dealerId/product/:productType/type/:searchType/year/:vehicleYear/make/:vehicleManufacturerName/category/:vehicleModelClassId/model/:vehicleModelName";

export const DealerProductDetailsRoute =
  "/:dealerUrlCode/:language/dealer/:dealerId/product/:productType/year/:vehicleYear/make/:vehicleManufacturerName/category/:vehicleModelClassId/model/:vehicleModelName/trim/:vehicleSubmodel/vehicle/:vehicleId/product/:partId/secondary/:secondaryPartId";

export const DealerTiresizeResultsRoute =
  "/:dealerUrlCode/:language/dealer/:dealerId/search/tiresize-search/:tireSize";
export const DealerPartNumberResultsRoute =
  "/:dealerUrlCode/:language/dealer/:dealerId/search/partnumber-search/:partNumber";

export const DealerChassisResultsRoute =
  "/:dealerUrlCode/:language/dealer/:dealerId/product/:productType/type/chassis-search/chassis/:vehicleChassis";
export const DealerVinResultsRoute =
  "/:dealerUrlCode/:language/dealer/:dealerId/product/:productType/type/vin-search/vin/:vehicleVin";

export const DealerContactUsRoute =
  "/:dealerUrlCode/:language/dealer/:dealerId/contact-us";
export const DealerCartRoute =
  "/:dealerUrlCode/:language/dealer/:dealerId/wishlist/:cartId";
export const DealerWishlistPrintRoute =
  "/:dealerUrlCode/:language/dealer/:dealerId/wishlist/:action/:cartId";
export const DealerComparePrintRoute =
  "/:dealerUrlCode/:language/dealer/:dealerId/product/tires/compare/:partId/vehicle/:vehicleId";

export const DealerOrderPrintRoute =
  "/:dealerUrlCode/:language/dealer/:dealerId/order-details/:action/:orderNumber";
export const DealerOrderReviewRoute =
  "/:dealerUrlCode/:language/dealer/:dealerId/order/review/:orderNumber";

export const DealerPackageEditorRoute =
  "/:dealerUrlCode/:language/dealer/:dealerId/package-editor";

export const AllDealerRoutes = [
  DealerHomePageRoute,
  DealerLandingPageRoute,
  DealerProductRoute,
  DealerVehicleResultsWithoutTrimRoute,
  DealerVehicleResultsRoute,
  DealerProductDetailsRoute,
  DealerTiresizeResultsRoute,
  DealerPartNumberResultsRoute,
  DealerChassisResultsRoute,
  DealerVinResultsRoute,
  DealerContactUsRoute,
  DealerCartRoute,
  DealerWishlistPrintRoute,
  DealerComparePrintRoute,
];

export const DealerRoutes: FunctionComponent = () => {
  const { dealerId, dealerUrlCode } = useDealerAccountInfo();
  const [language] = useLanguage();
  const isLoggedOn = useIsLoggedOn();
  const products = useDealerProducts();
  const dealerHome = `/${dealerUrlCode}/${language}/dealer/${dealerId}`;
  const { t } = useTranslation();
  const hasPackageEditor = usePackageEditorSetting();

  return (
    <>
      <Router>
        <Switch>
          <Route component={DealerHomePage} path={DealerLandingPageRoute} />
          <Route component={DealerCartPage} path={DealerCartRoute} />
          <Route
            component={DealerWishlistPrintPage}
            path={DealerWishlistPrintRoute}
          />
          <Route
            component={DealerComparePrintPage}
            path={DealerComparePrintRoute}
          />
          <Route
            component={DealerResultsPage}
            path={DealerVehicleResultsWithoutTrimRoute}
          />
          <Route
            component={DealerResultsPage}
            path={DealerVehicleResultsRoute}
          />
          <Route
            component={DealerResultsPage}
            path={DealerTiresizeResultsRoute}
          />
          <Route
            component={DealerResultsPage}
            path={DealerChassisResultsRoute}
          />
          <Route component={DealerResultsPage} path={DealerVinResultsRoute} />
          {isLoggedOn && (
            <Route
              component={DealerResultsPage}
              path={DealerPartNumberResultsRoute}
            />
          )}
          {hasPackageEditor && (
            <Route
              component={DealerPackageEditorPage}
              path={DealerPackageEditorRoute}
            />
          )}
          <Route
            component={DealerProductDetailsPage}
            path={DealerProductDetailsRoute}
          />
          {products.map(({ Component, name }) => (
            <Fragment key={name}>
              <Route path={DealerProductRoute.replace(":productType", name)}>
                <Meta cmsTitle={null} title={name} />
                <Component />
              </Route>
            </Fragment>
          ))}
          <Route component={DealerContactUsPage} path={DealerContactUsRoute} />
          <Route
            component={DealerOrderReviewPage}
            path={DealerOrderReviewRoute}
          />
          <Route
            component={DealerWishlistPrintPage}
            path={DealerOrderPrintRoute}
          />
          <Route path={DealerHomePageRoute}>
            <Redirect to={dealerHome} />
          </Route>
          <Route path={DealerLanguagePageRoute}>
            <Redirect to={dealerHome} />
          </Route>
          <Route>
            <NoData>{t("pageNotFound")}</NoData>
          </Route>
        </Switch>
      </Router>
      <NewPackageDialog />
    </>
  );
};
